import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { QpxButton, QpxIcon, QpxTooltip } from '../../../directives';
import type { FilePreviewPanelConfig } from '../models';
import { resolvePreviewPanelDomId } from './preview-panel';

export interface PreviewPaneButton {
    readonly id: string;
    readonly label: string;
    readonly icon: string;
    readonly ariaLabel: string;
    readonly active: boolean;
    readonly disabled: boolean;
    readonly controls?: string;
    readonly className?: string;
    readonly controlGroup?: string;
    readonly onSelect: (event: MouseEvent) => void;
}

export function toPreviewPaneButtons(
    panels: readonly FilePreviewPanelConfig[],
    activeId: string | null,
    onSelect: (panel: FilePreviewPanelConfig) => void,
    disabled = false,
    viewerReady = true
): PreviewPaneButton[] {
    return panels.map((panel) => ({
        id: `panel:${panel.id}`,
        label: panel.label,
        icon: panel.icon,
        ariaLabel: panel.ariaLabel,
        active: panel.id === activeId,
        disabled:
            disabled ||
            (!viewerReady && panel.requiresViewerReady !== false) ||
            !!panel.disabled,
        controls: resolvePreviewPanelDomId(panel.side, panel.id),
        className: panel.className,
        controlGroup: panel.side === 'right' ? panel.controlGroup : undefined,
        onSelect: () => onSelect(panel),
    }));
}

@Component({
    selector: 'qpx-preview-pane-buttons',
    imports: [
        MatButtonModule,
        MatMenuModule,
        NgIcon,
        QpxButton,
        QpxIcon,
        QpxTooltip,
    ],
    templateUrl: './preview-pane-buttons.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'contents' },
})
export class PreviewPaneButtons {
    public readonly choices = input<readonly PreviewPaneButton[]>([]);
    public readonly menuLabel = input('Pane options');
    public readonly controlGroup = input<string | null>(null);
    protected readonly groupedChoices = computed(() =>
        this.choices().filter(
            (choice) => (choice.controlGroup || null) === this.controlGroup()
        )
    );
    protected readonly mobile = injectViewportBreakpoints().isPhone;
    protected readonly visibleChoices = computed(() => {
        const choices = this.groupedChoices();
        const primary = choices.find((choice) => choice.active) ?? choices[0];
        return this.mobile() ? (primary ? [primary] : []) : choices;
    });
    protected readonly otherChoices = computed(() =>
        this.groupedChoices().filter(
            (choice) => choice.id !== this.visibleChoices()[0]?.id
        )
    );
}
